import { Button, Input, Space, Table, Tag, Tooltip } from 'antd';
import moment from 'moment/moment';
import React from 'react';
import { groupBy, values } from 'lodash';
import { useServiceOffersQuery } from '../../../graphql/datamodel.gen';

interface DrivingCompaniesPostulations {
  onIgnoreBid: (serviceOfferId: string, bidId: string) => any;
  onRejectBid: (serviceOfferId: string, bidId: string) => any;
  onAcceptBid: (serviceOfferId: string, bidId: string) => any;
  onViewService: (serviceId: string) => any;
}

export const DrivingCompaniesPostulations: React.FC<
  DrivingCompaniesPostulations
> = (props) => {
  const [searchValue, setSearchValue] = React.useState<string | undefined>();
  const { onAcceptBid, onRejectBid, onIgnoreBid, onViewService } = props;

  const serviceOffersQuery = useServiceOffersQuery({
    fetchPolicy: 'cache-and-network',
  });

  const bidsGroupedByCompany = values(
    groupBy(
      serviceOffersQuery.data?.serviceOffers?.flatMap((so) =>
        so.bids.map((bid) => {
          return {
            ...bid,
            serviceOffer: so,
          };
        }),
      ),
      (bid) => {
        return bid.drivingCompany.id;
      },
    ),
  )
    .map((drivingCompanyBids) => {
      return {
        drivingCompany: drivingCompanyBids[0].drivingCompany,
        bids: drivingCompanyBids,
      };
    })
    .filter((companyBids) => {
      if (!searchValue) {
        return true;
      }

      return (
        companyBids.drivingCompany.name
          .toLowerCase()
          .includes(searchValue.toLowerCase()) ||
        searchValue
          .toLowerCase()
          .includes(companyBids.drivingCompany.name.toLowerCase())
      );
    });

  const onSearch = (value: string) => {
    setSearchValue(value);
  };

  return (
    <Space direction={'vertical'} size={'middle'}>
      <Space size={'large'}>
        <Input.Search
          style={{ width: 350 }}
          allowClear
          placeholder={'Filtra por cualquier empresa colaboradora'}
          onSearch={onSearch}
        />
      </Space>
      <Table
        dataSource={bidsGroupedByCompany}
        size={'small'}
        expandable={{
          expandRowByClick: true,
          rowExpandable: (record) => {
            return record.bids.length > 0;
          },
          expandedRowRender: (record) => {
            return (
              <>
                <h4>Postulaciones</h4>
                <Table
                  bordered
                  size={'small'}
                  dataSource={record.bids}
                  rowKey={(record) => record.id}
                  columns={[
                    {
                      title: 'Fecha de postulación',
                      width: 150,
                      dataIndex: 'createdAt',
                      render: (createdAt: string) => {
                        return moment(createdAt).format('DD/MM/YY, HH:mm');
                      },
                    },
                    {
                      title: 'ID Servicio',
                      width: 100,
                      render: (_, bidRecord) => {
                        return bidRecord.serviceOffer.service.volcanoServiceId;
                      },
                    },
                    {
                      title: 'Salida',
                      dataIndex: [
                        'serviceOffer',
                        'service',
                        'departure',
                        'date',
                      ],
                      width: 150,
                      sortDirections: ['descend', 'ascend'],
                      defaultSortOrder: 'descend' as
                        | 'descend'
                        | 'ascend'
                        | undefined,
                      render: (_: any, record) => {
                        return (
                          <div>
                            {record.serviceOffer.service.departure.date}{' '}
                            <b>{record.serviceOffer.service.departure.time}</b>
                          </div>
                        );
                      },
                      sorter: (a, b) => {
                        if (
                          a.serviceOffer.service.departure &&
                          a.serviceOffer.service.departure.time &&
                          a.serviceOffer.service.departure.date &&
                          b.serviceOffer.service.departure &&
                          b.serviceOffer.service.departure.time &&
                          b.serviceOffer.service.departure.date
                        ) {
                          const aDateTime = new Date(
                            a.serviceOffer.service.departure.date,
                          );
                          aDateTime.setHours(
                            Number(
                              (
                                a.serviceOffer.service.departure.time as string
                              ).split(':')[0],
                            ),
                            Number(
                              (
                                a.serviceOffer.service.departure.time as string
                              ).split(':')[1],
                            ),
                          );

                          const bDateTime = new Date(
                            b.serviceOffer.service.departure.date,
                          );
                          bDateTime.setHours(
                            Number(
                              (
                                b.serviceOffer.service.departure.time as string
                              ).split(':')[0],
                            ),
                            Number(
                              (
                                b.serviceOffer.service.departure.time as string
                              ).split(':')[1],
                            ),
                          );

                          return bDateTime.getTime() - aDateTime.getTime();
                        }

                        return 0;
                      },
                    },
                    {
                      title: 'Llegada',
                      dataIndex: ['serviceOffer', 'service', 'arrival', 'date'],
                      width: 150,
                      render: (_: any, record) => {
                        const arrivalDate = moment(
                          record.serviceOffer.service.arrival.date,
                        );
                        const departureDate = moment(
                          record.serviceOffer.service.departure.date,
                        );
                        const durationAsDays = moment
                          .duration(arrivalDate.diff(departureDate))
                          .asDays();

                        if (durationAsDays >= 1) {
                          // Append +1 to the time
                          return (
                            <div>
                              <b>{record.serviceOffer.service.arrival.time}</b>{' '}
                              <sup>+{durationAsDays}</sup>
                            </div>
                          );
                        } else {
                          // Just show the time
                          return (
                            <div>
                              <b>{record.serviceOffer.service.arrival.time}</b>
                            </div>
                          );
                        }
                      },
                    },
                    {
                      title: '# PAX',
                      width: 75,
                      render: (_, record) => {
                        return record.serviceOffer.service.numOfPassengers;
                      },
                    },
                    {
                      title: 'Descripción',
                      ellipsis: {
                        showTitle: false,
                      },
                      render: (_, record) => {
                        const desc = record.serviceOffer.service.description;
                        return (
                          <Tooltip placement="topLeft" title={desc}>
                            {desc}
                          </Tooltip>
                        );
                      },
                    },
                    {
                      title: 'Tipo de vehículo',
                      dataIndex: [
                        'serviceOffer',
                        'service',
                        'requestedVehicleDescription',
                      ],
                    },
                    {
                      title: 'Estado',
                      width: 100,
                      render: (_: any, bidRecord) => {
                        if (bidRecord.status === 'PENDING') {
                          return <Tag>Pendiente</Tag>;
                        }
                        if (bidRecord.status === 'ACCEPTED') {
                          return <Tag color={'success'}>Aceptada</Tag>;
                        }
                        if (bidRecord.status === 'REJECTED') {
                          return <Tag color={'error'}>Rechazada</Tag>;
                        }
                      },
                    },
                    {
                      title: 'Acciones',
                      width: 350,
                      render: (_: any, bidRecord) => {
                        return (
                          <Space>
                            <Button
                              type={'primary'}
                              size={'small'}
                              disabled={bidRecord.status === 'ACCEPTED'}
                              onClick={() => {
                                onAcceptBid(
                                  bidRecord.serviceOffer.id,
                                  bidRecord.id,
                                );
                              }}
                            >
                              Aceptar
                            </Button>
                            <Button
                              danger
                              size={'small'}
                              disabled={bidRecord.status === 'REJECTED'}
                              onClick={() => {
                                onRejectBid(
                                  bidRecord.serviceOffer.id,
                                  bidRecord.id,
                                );
                              }}
                            >
                              Rechazar
                            </Button>
                            <Button
                              size={'small'}
                              disabled={bidRecord.status === 'PENDING'}
                              onClick={() => {
                                onIgnoreBid(
                                  bidRecord.serviceOffer.id,
                                  bidRecord.id,
                                );
                              }}
                            >
                              Ignorar
                            </Button>
                            <span>|</span>
                            <Button
                              size={'small'}
                              onClick={() =>
                                onViewService(
                                  bidRecord.serviceOffer.service
                                    .volcanoServiceId,
                                )
                              }
                            >
                              Ver
                            </Button>
                          </Space>
                        );
                      },
                    },
                  ]}
                />
              </>
            );
          },
        }}
        rowKey={(record) => record.drivingCompany.id}
        columns={[
          {
            title: 'Empresa colaboradora',
            key: 'drivingCompany',
            width: 300,
            dataIndex: ['drivingCompany']['name'],
            ellipsis: {
              showTitle: false,
            },
            render: (_, record) => {
              return (
                <Tooltip title={record.drivingCompany.name}>
                  {record.drivingCompany.name}
                </Tooltip>
              );
            },
          },
          {
            title: '# Postulaciones',
            key: 'nBids',
            width: 100,
            render: (_, record) => {
              return record.bids.length;
            },
            sortDirections: ['ascend', 'descend'],
            sorter: (a, b) => {
              return a.bids.length - b.bids.length;
            },
          },
          {
            title: 'Fecha última postulación',
            width: 150,
            sortDirections: ['ascend', 'descend'],
            defaultSortOrder: 'descend',
            sorter: (a, b) => {
              const getLatestBid = (record) => {
                const [latestBid] = record.bids.sort((bidA, bidB) => {
                  return bidA.createdAt < bidB.createdAt
                    ? -1
                    : bidA.createdAt > bidB.createdAt
                    ? 1
                    : 0;
                });

                return latestBid;
              };

              const latestBidA = getLatestBid(a);
              const latestBidB = getLatestBid(b);

              return latestBidA.createdAt < latestBidB.createdAt
                ? -1
                : latestBidA.createdAt > latestBidB.createdAt
                ? 1
                : 0;
            },
            render: (_, record) => {
              const [latestBid] = record.bids.sort((bidA, bidB) => {
                return bidA.createdAt < bidB.createdAt
                  ? -1
                  : bidA.createdAt > bidB.createdAt
                  ? 1
                  : 0;
              });

              return moment(latestBid.createdAt).format('DD/MM, HH:mm');
            },
          },
        ]}
      />
    </Space>
  );
};
